import { useCallback, useRef, useState } from 'react';

import { draftMatchesDevice, type Draft } from '../../domain/draft.ts';
import { parseProfile, serialiseProfile, ProfileParseError } from '../pattern-editor/jsonIO.ts';
import {
  deleteProfile,
  exportLibrary,
  importLibrary,
  isAvailable,
  load,
  saveProfile,
  type StoredProfile,
} from './storage.ts';

interface Props {
  draft: Draft;
  /** The connected device's current configuration, for the "unsaved" indicator. */
  deviceDraft: Draft | null;
  onLoad: (draft: Draft, name: string) => void;
  /** Name of the profile currently loaded, if any. */
  currentName: string | null;
  onNameChange: (name: string | null) => void;
  compact?: boolean;
}

export function ProfileLibrary({
  draft,
  deviceDraft,
  onLoad,
  currentName,
  onNameChange,
  compact = false,
}: Props) {
  const [profiles, setProfiles] = useState<StoredProfile[]>(() => load());
  const [name, setName] = useState(currentName ?? '');
  const [message, setMessage] = useState<{ text: string; kind: 'ok' | 'bad' } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const available = isAvailable();

  const refresh = useCallback(() => setProfiles(load()), []);

  const onSaveNew = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) {
      setMessage({ text: 'Give the profile a name first.', kind: 'bad' });
      return;
    }
    setProfiles(saveProfile(trimmed, draft));
    onNameChange(trimmed);
    setMessage({ text: `Saved "${trimmed}".`, kind: 'ok' });
  }, [name, draft, onNameChange]);

  const onLoadProfile = useCallback(
    (profile: StoredProfile) => {
      // Reuse the profile parser rather than trusting localStorage: the same
      // validation runs on stored and imported data, and corrupt storage cannot
      // smuggle a bad value past the editor.
      try {
        const { draft: loaded, name: loadedName } = parseProfile(JSON.stringify(profile));
        onLoad(loaded, loadedName);
        setName(loadedName);
        onNameChange(loadedName);
        setMessage({ text: `Loaded "${loadedName}".`, kind: 'ok' });
      } catch (e) {
        setMessage({
          text: e instanceof ProfileParseError ? `Stored profile is invalid: ${e.message}` : String(e),
          kind: 'bad',
        });
      }
    },
    [onLoad, onNameChange],
  );

  const onDelete = useCallback(
    (profileName: string) => {
      setProfiles(deleteProfile(profileName));
      if (currentName === profileName) onNameChange(null);
    },
    [currentName, onNameChange],
  );

  const onExport = useCallback(() => {
    const text = serialiseProfile(draft, name.trim() || 'untitled');
    download(`${(name.trim() || 'untitled').replace(/\s+/g, '-')}.vmj.json`, text);
    setMessage({ text: 'Exported this profile as JSON.', kind: 'ok' });
  }, [draft, name]);

  const onExportAll = useCallback(() => {
    download('vimousejiggler-profiles.json', exportLibrary());
    setMessage({ text: `Exported ${profiles.length} profile(s).`, kind: 'ok' });
  }, [profiles.length]);

  const onImport = useCallback(
    async (file: File) => {
      const text = await file.text();
      try {
        // A single profile and a whole library are both accepted; try the library
        // shape first because it is the one that cannot be confused with a profile.
        const looksLikeLibrary = /"profiles"\s*:/.test(text);
        if (looksLikeLibrary) {
          const { added, replaced } = importLibrary(text);
          refresh();
          setMessage({ text: `Imported ${added} new and ${replaced} replaced profile(s).`, kind: 'ok' });
        } else {
          const { draft: imported, name: importedName } = parseProfile(text);
          saveProfile(importedName, imported);
          refresh();
          onLoad(imported, importedName);
          setName(importedName);
          onNameChange(importedName);
          setMessage({ text: `Imported "${importedName}".`, kind: 'ok' });
        }
      } catch (e) {
        setMessage({ text: e instanceof Error ? e.message : String(e), kind: 'bad' });
      }
    },
    [onLoad, onNameChange, refresh],
  );

  const unsaved = deviceDraft !== null && !draftMatchesDevice(draft, deviceDraft);

  return (
    <section className="panel">
      <h2>Profiles</h2>
      {compact && available && <p className="dim">Saved in this browser</p>}

      {!available && (
        <p className="warn">
          {compact ? 'Browser storage unavailable. Export to keep your profile.' : <>
            Browser storage is unavailable (private mode, or storage disabled). You can
            still configure the device, and export/import JSON — profiles just will not
            persist between sessions.
          </>}
        </p>
      )}

      <div className="row">
        <input
          className="text"
          type="text"
          placeholder="Profile name"
          value={name}
          aria-label="Profile name"
          onChange={(e) => setName(e.target.value)}
        />
        <button type="button" className="btn btn--small" onClick={onSaveNew}>
          {compact ? 'Save profile' : profiles.some((p) => p.name === name.trim()) ? 'Overwrite' : 'Save'}
        </button>
        <button type="button" className="btn btn--small" onClick={onExport}>
          {compact ? 'Export' : 'Export this'}
        </button>
        <button type="button" className="btn btn--small" onClick={onExportAll} disabled={profiles.length === 0}>
          Export all
        </button>
        <button type="button" className="btn btn--small" onClick={() => fileInput.current?.click()}>
          {compact ? 'Import' : 'Import…'}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          aria-label="Import profiles"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onImport(file);
            e.target.value = '';
          }}
        />
      </div>

      {message && <p className={message.kind}>{message.text}</p>}
      {unsaved && <p className="warn">{compact ? 'Draft differs from the device.' : 'Unsaved changes — this differs from what the device holds.'}</p>}

      {profiles.length > 0 && (
        <ul className="profiles">
          {profiles.map((p) => (
            <li key={p.name} className={p.name === currentName ? 'profiles__item profiles__item--on' : 'profiles__item'}>
              <button type="button" className="profiles__name" onClick={() => onLoadProfile(p)}>
                {p.name}
              </button>
              <span className="dim">
                {p.steps.length} step{p.steps.length === 1 ? '' : 's'} ·{' '}
                {new Date(p.updatedAt).toLocaleDateString()}
                {p.lastPushedAt ? ` · pushed ${new Date(p.lastPushedAt).toLocaleDateString()}` : ''}
              </span>
              <button
                type="button"
                className="icon icon--danger"
                aria-label={`delete ${p.name}`}
                title="Delete"
                onClick={() => onDelete(p.name)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {!compact && <p className="dim">
        Profiles live in this browser only, not on the device. The device holds exactly
        one configuration, which is what it replays on power-up.
        {profiles.length === 0 && available && ' Save one to keep a tuned pattern around.'}
      </p>}
    </section>
  );
}

function download(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
